// ============================================================================
// Die Anmeldung (SPEC §2.1, §2.2). Zwei Schritte: Adresse → Code.
// Übernommen aus /opt/lilasprojekte/frontend/src/screens/Anmelden.tsx (dort aus dem
// Zeitplaner, dort aus dem Fragenbüro); geändert: Wortmarke „Katalog", Texte aus
// lib/texte/basis.ts, kein Profilriegel und kein Verweis auf eine öffentliche Karte.
// Die vier Fallen, die in den Geschwistern schon zugeschlagen haben, bleiben — KEINE
// davon wegräumen:
//
// (a) Der Doppelabsende-Riegel liegt in einem REF, nicht im Zustand: das
//     Autofill des Telefons löst `submit` UND `click` im selben Tick aus, und
//     ein Zustandswert wäre beim zweiten Aufruf noch der alte. Ein Code gilt
//     EINMAL — der zweite Versuch liefe ins Leere, und die Person läse „Der
//     Code stimmt nicht", obwohl alles stimmte.
// (b) Der Turnstile-Effekt darf bei `schritt === "code"` NICHT aussteigen.
//     Sonst räumt sein Aufräumer das Widget ab, und „Neuen Code schicken"
//     verschickt für immer dieselbe, verbrauchte Marke (Cloudflare:
//     timeout-or-duplicate) — was serverseitig jedes Mal eine Fehlzeile gegen
//     das Kontingent des ganzen Büros schreibt.
// (c) `pruefungZurueck()` läuft nach JEDEM Absenden, auch nach Erfolg: eine
//     Wertmarke gilt genau einmal.
// (d) Nur das ERSTE Codefeld trägt `autoComplete="one-time-code"` — iOS füllt
//     dort den ganzen Code ein, `codeSetzen()` verteilt ihn weiter.
//
// Der Behälter heißt `pruefung`, NIEMALS `turnstile`: der Browser legt für
// jedes Element mit id eine gleichnamige Eigenschaft auf `window` an, und
// `window.turnstile` wäre dann das <div> — die api.js hielte sich für schon
// geladen und zeichnete nichts (lib/turnstile.ts).
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, IconButton, Input, Spinner, Switch } from "../components/ui";
import { Logo } from "../components/app/Logo";
import { ApiFehler } from "../api/client";
import { codeAnfordern, codeEinloesen, holeAuthConfig, holeMich } from "../api/hooks/basis";
import { schluessel } from "../api/schluessel";
import { CODE_LAENGE, codeRuecktaste, codeSetzen, codeText, codeVollstaendig, leererCode } from "../lib/code";
import { sekundenKurz } from "../lib/format";
import { useT } from "../lib/texte";
import { BEHAELTER_ID, ladeTurnstile } from "../lib/turnstile";
import { anmeldeZiel } from "../lib/wege";
import { useTitel } from "../hooks/useTitel";
import { useTheme } from "../lib/theme";
import { useAuthStore } from "../store/useAuthStore";

type Schritt = "email" | "code";

const ERNEUT_SPERRE_S = 60;

export function Anmelden() {
  const { t } = useT();
  useTitel(t.anmelden.titel);
  const { thema, umschalten } = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);
  // Wohin nach dem Code: `von` schreiben die Wächter in App.tsx — ein Verweis auf
  // einen Katalog aus einer Mail soll nach der Anmeldung derselbe sein. `von` kommt
  // aus `history.state` und ist damit Eingabe; `anmeldeZiel` lässt nur einen Pfad
  // dieser Anwendung durch (lib/wege.ts).
  const ort = useLocation();
  const ziel = anmeldeZiel((ort.state as { von?: unknown } | null)?.von);

  const [schritt, setSchritt] = useState<Schritt>("email");
  const [email, setEmail] = useState("");
  // Vorbelegt AN: der Katalog steht den ganzen Tag auf einem Bürobildschirm offen.
  const [bleiben, setBleiben] = useState(true);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState<string[]>(leererCode);
  const [fehler, setFehler] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [wackelt, setWackelt] = useState(false);
  const [erneutAb, setErneutAb] = useState(0);
  const [jetzt, setJetzt] = useState(() => Date.now());

  const [sitekey, setSitekey] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<string | undefined>(undefined);

  // (a) Der Doppelabsende-Riegel. Ref, nicht Zustand.
  const laeuftRef = useRef(false);
  const feldRefs = useRef<(HTMLInputElement | null)[]>([]);

  /* --- Turnstile -------------------------------------------------------- */

  // Scheitert GET /auth/config (500, Deploy), fehlt das Prüfkästchen, und der Server weist
  // „Code schicken" ohnehin ab. Vorher stand dann nichts da (Prüfung Runde 2); jetzt ein
  // Satz, und alle 5 s ein neuer Versuch, bis die Konfiguration kommt.
  const [konfigVersuch, setKonfigVersuch] = useState(0);
  const [konfigFehlt, setKonfigFehlt] = useState(false);
  useEffect(() => {
    let lebt = true;
    let uhr: ReturnType<typeof setTimeout> | undefined;
    holeAuthConfig()
      .then((c) => {
        if (!lebt) return;
        setKonfigFehlt(false);
        setSitekey(c.turnstile_sitekey);
        setDomains(c.allowed_domains ?? []);
      })
      .catch(() => {
        if (!lebt) return;
        setSitekey(null);
        setKonfigFehlt(true);
        uhr = setTimeout(() => setKonfigVersuch((v) => v + 1), 5000);
      });
    return () => {
      lebt = false;
      if (uhr) clearTimeout(uhr);
    };
  }, [konfigVersuch]);

  // (b) Schritt 1 UND 2 brauchen ein lebendes Widget — `schritt` steht in der
  // Abhängigkeitsliste, damit im zweiten Schritt ein FRISCHES gezeichnet wird.
  useEffect(() => {
    if (!sitekey) return;
    let lebt = true;
    let id: string | undefined;
    ladeTurnstile()
      .then((api) => {
        if (!lebt || !document.getElementById(BEHAELTER_ID)) return;
        // Explizites render(): mit render=explicit zeichnet Cloudflare von
        // sich aus nichts mehr, und der Sitekey kommt ohnehin erst zur
        // Laufzeit aus GET /auth/config.
        id = api.render(`#${BEHAELTER_ID}`, {
          sitekey,
          action: "login",
          theme: "auto",
          size: "flexible",
          callback: (tk) => setToken(tk),
          "error-callback": () => {
            setToken(null);
            setFehler(t.anmelden.pruefungFehler);
          },
          "expired-callback": () => setToken(null),
        });
        widgetRef.current = id;
      })
      .catch((e: unknown) => {
        if (lebt) setFehler(e instanceof Error ? e.message : t.anmelden.pruefungFehler);
      });
    return () => {
      lebt = false;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* schon weg */
        }
      }
      widgetRef.current = undefined;
    };
    // `t` gehört bewusst NICHT in die Liste: ein Sprachwechsel würde das
    // Widget sonst neu zeichnen und die gültige Wertmarke wegwerfen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey, schritt]);

  /** (c) Nach JEDEM Absenden: die Wertmarke ist verbraucht. */
  const pruefungZurueck = useCallback(() => {
    setToken(null);
    if (widgetRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetRef.current);
      } catch {
        /* Widget ist weg — dann gibt es auch nichts zurückzusetzen */
      }
    }
  }, []);

  /* --- Countdown „Neuen Code schicken" ---------------------------------- */

  useEffect(() => {
    if (!erneutAb) return;
    const uhr = setInterval(() => setJetzt(Date.now()), 500);
    return () => clearInterval(uhr);
  }, [erneutAb]);
  const restS = erneutAb ? Math.max(0, (erneutAb - jetzt) / 1000) : 0;

  /* --- Schritt 1: Code anfordern ---------------------------------------- */

  const anfordern = useCallback(
    async (erneut: boolean) => {
      if (laeuftRef.current) return;
      if (erneut && restS > 0) return;
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler("");
      try {
        const a = await codeAnfordern(email.trim(), token);
        setChallenge(a.challenge);
        setCode(leererCode());
        setSchritt("code");
        setErneutAb(Date.now() + ERNEUT_SPERRE_S * 1000);
        setJetzt(Date.now());
        pruefungZurueck();
        setTimeout(() => feldRefs.current[0]?.focus(), 60);
      } catch (e) {
        setFehler(e instanceof ApiFehler ? e.detail : t.anmelden.startFehler);
        pruefungZurueck();
      } finally {
        laeuftRef.current = false;
        setLaeuft(false);
      }
    },
    [email, token, restS, pruefungZurueck, t],
  );

  /* --- Schritt 2: Code einlösen ----------------------------------------- */

  const absendenCode = useCallback(
    async (ziffern: string[]) => {
      if (laeuftRef.current) return;
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler("");
      try {
        await codeEinloesen(challenge, codeText(ziffern), bleiben);
        angemeldet();
        // Die Person VOR dem Weiterspringen holen und ablegen: Kopfleiste und Wächter
        // lesen `Ich` aus genau diesem Eintrag, und ohne ihn stünde die Leiste einen
        // Wimpernschlag lang ohne Avatar da.
        await qc.fetchQuery({ queryKey: schluessel.ich, queryFn: holeMich });
        navigate(ziel, { replace: true });
      } catch (e) {
        setFehler(e instanceof ApiFehler ? e.detail : t.anmelden.codeFehler);
        setCode(leererCode());
        setWackelt(true);
        setTimeout(() => setWackelt(false), 320);
        setTimeout(() => feldRefs.current[0]?.focus(), 40);
      } finally {
        laeuftRef.current = false;
        setLaeuft(false);
      }
    },
    [challenge, bleiben, angemeldet, qc, navigate, t, ziel],
  );

  const ziffernSetzen = (index: number, roh: string) => {
    // Tippt jemand in ein schon gefülltes Kästchen, steht dort kurz „12".
    // Gemeint ist die NEUE Ziffer, nicht ein zweistelliger Einschub — sonst
    // rutscht die alte Ziffer nach rechts weiter.
    const alt = code[index] ?? "";
    const eingabe = alt && roh.length === 2 && roh.startsWith(alt) ? roh.slice(1) : roh;
    const { code: neu, fokus } = codeSetzen(code, index, eingabe);
    setCode(neu);
    feldRefs.current[fokus]?.focus();
    // Die sechste Ziffer schickt die Karte selbst ab.
    if (codeVollstaendig(neu)) void absendenCode(neu);
  };

  // Wer schon angemeldet ist, hat hier nichts verloren. Auch hier `ziel` und
  // nicht „/": dieser Effekt feuert nach dem Code ZUERST — `angemeldet()`
  // läuft vor dem `await` in `absendenCode` —, und mit „/" ginge der Verweis
  // genau in dem Moment verloren, für den `von` da ist.
  useEffect(() => {
    if (status === "angemeldet") navigate(ziel, { replace: true });
  }, [status, navigate, ziel]);

  // Der Rückfall ist DERSELBE Satzbauer mit den beiden Hausdomänen und nicht
  // ein zweiter, fest getippter Satz — der zweite Wortlaut wäre der, der beim
  // nächsten Mal vergessen wird.
  const domainHinweis = t.anmelden.domains(domains.length ? [...domains] : ["lilasp.de", "lilasp.gmbh"]);

  return (
    <main className="anmelde-huelle">
      {/* Kopfleiste wie im Canvas: Marke und Thema schon vor der Anmeldung. */}
      <header className="kopf glass">
        <Logo />
        <div style={{ flex: 1 }} />
        <IconButton icon={thema === "dark" ? "sun" : "moon"} title={thema === "dark" ? t.nav.themaHell : t.nav.themaDunkel} onClick={umschalten} />
      </header>
      <div className="anmelde-karte karte-ein spalte" style={{ gap: 18 }}>

        {schritt === "email" ? (
          <form
            className="spalte"
            style={{ gap: 14 }}
            onSubmit={(ev) => {
              ev.preventDefault();
              void anfordern(false);
            }}
          >
            <div className="spalte" style={{ gap: 6 }}>
              <h1 className="seitentitel" style={{ fontSize: 28 }}>
                {t.anmelden.titel}
              </h1>
              <p className="unterzeile">{t.anmelden.unterzeile}</p>
            </div>

            <div>
              <label className="flabel" htmlFor="kt-email">
                {t.anmelden.email}
              </label>
              <Input
                id="kt-email"
                type="email"
                value={email}
                autoFocus
                required
                autoComplete="email"
                inputMode="email"
                placeholder={t.anmelden.emailPlatzhalter}
                onChange={(ev) => setEmail(ev.target.value)}
              />
            </div>

            {/* (Behälter) pruefung, NIE turnstile. Ohne Sitekey bleibt er ohne
                Höhe und der Knopf ist frei. Volle Breite (`size: flexible`). */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 65 : 0, width: "100%" }} />

            {/* Ohne Satz ohne Höhe: die leere Zeile machte rund 70 px Luft vor dem Knopf.
                Das Element bleibt stehen — eine Statusregion, die erst mit dem Satz
                entsteht, liest manche Vorlesehilfe nicht vor. */}
            <p className="fehlerzeile" role="status" style={fehler || konfigFehlt ? undefined : { minHeight: 0, margin: 0 }}>
              {fehler || (konfigFehlt ? t.anmelden.konfigFehlt : "")}
            </p>

            <Button type="submit" size="lg" style={{ width: "100%" }} disabled={laeuft || konfigFehlt || !email.trim() || (!!sitekey && !token)}>
              {laeuft && <Spinner size={14} color="currentColor" />}
              {t.anmelden.codeSchicken}
            </Button>
            <p className="hinweiszeile" style={{ textAlign: "center" }}>
              {domainHinweis}
            </p>
          </form>
        ) : (
          <form
            className={`spalte${wackelt ? " wackel" : ""}`}
            style={{ gap: 14 }}
            onSubmit={(ev) => {
              ev.preventDefault();
              if (codeVollstaendig(code)) void absendenCode(code);
            }}
          >
            <div className="spalte" style={{ gap: 6 }}>
              <h1 className="seitentitel" style={{ fontSize: 28 }}>
                {t.anmelden.codeTitel}
              </h1>
              <p className="unterzeile">{t.anmelden.codeUnterzeile(email.trim())}</p>
            </div>

            <div className="code-reihe">
              {code.map((z, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    feldRefs.current[i] = el;
                  }}
                  className="codebox"
                  value={z}
                  inputMode="numeric"
                  // (d) Nur das erste Feld trägt one-time-code.
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  autoFocus={i === 0}
                  maxLength={CODE_LAENGE}
                  aria-label={t.anmelden.ziffer(i + 1, CODE_LAENGE)}
                  disabled={laeuft}
                  onChange={(ev) => ziffernSetzen(i, ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Backspace") {
                      ev.preventDefault();
                      const { code: neu, fokus } = codeRuecktaste(code, i);
                      setCode(neu);
                      feldRefs.current[fokus]?.focus();
                    }
                    if (ev.key === "ArrowLeft") feldRefs.current[Math.max(0, i - 1)]?.focus();
                    if (ev.key === "ArrowRight") feldRefs.current[Math.min(CODE_LAENGE - 1, i + 1)]?.focus();
                  }}
                  onPaste={(ev) => {
                    ev.preventDefault();
                    ziffernSetzen(i, ev.clipboardData.getData("text"));
                  }}
                />
              ))}
            </div>

            {/* Canvas Schritt 2: „Angemeldet bleiben" als Schalter HIER — gesendet wird
                der Wert erst mit dem Code (codeEinloesen). */}
            <div className="reihe" style={{ justifyContent: "space-between", gap: 12 }}>
              <span className="spalte" style={{ gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{t.anmelden.bleiben}</span>
                <span className="hinweiszeile">{t.anmelden.bleibenHinweis}</span>
              </span>
              <Switch on={bleiben} onChange={setBleiben} label={t.anmelden.bleiben} />
            </div>

            {/* Dasselbe <div> wie in Schritt 1 — immer nur EINES im Baum, sonst
                gäbe es die Kennung zweimal. Der Effekt oben zeichnet hierher
                ein frisches Widget, damit „Neuen Code schicken" eine
                unverbrauchte Marke hat (Falle b). */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0 }} />

            <p className="fehlerzeile" role="status" style={{ textAlign: "center" }}>
              {fehler}
            </p>

            <Button type="submit" size="lg" style={{ width: "100%" }} disabled={laeuft || !codeVollstaendig(code)}>
              {laeuft && <Spinner size={14} color="currentColor" />}
              {t.anmelden.titel}
            </Button>

            <div className="reihe" style={{ justifyContent: "space-between", fontSize: 12 }}>
              <Button type="button" variant="ghost" size="sm" onClick={() => setSchritt("email")}>
                {t.anmelden.andereAdresse}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={restS > 0 || laeuft || (!!sitekey && !token)}
                onClick={() => void anfordern(true)}
              >
                {restS > 0 ? t.anmelden.erneutIn(sekundenKurz(restS)) : t.anmelden.erneut}
              </Button>
            </div>
          </form>
        )}

      </div>
    </main>
  );
}
